/** 레거시 `prescriptionStatusText`와 동일 */
const ADMIN_CHECK_STATUS_LABEL: Record<string, string> = {
  MODIFIED: '수정',
  VERIFIED: '확인',
  UNVERIFIED: '미확인',
  REJECTED: '반려',
};

export const getAdminCheckStatusLabel = (status?: string): string =>
  (status && ADMIN_CHECK_STATUS_LABEL[status]) || status || '-';

/** 관리자 확인 상태별 Badge variant — 수정/확인을 색으로 구분 */
export const getAdminCheckStatusBadgeVariant = (
  status?: string,
): 'warning' | 'success' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'MODIFIED':
      return 'warning';
    case 'VERIFIED':
      return 'success';
    case 'REJECTED':
      return 'destructive';
    case 'UNVERIFIED':
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * 처방 확정 제외 사유 — 서버 enum PrescriptionImageExclusionReason.
 * shortLabel 은 목록 태그처럼 좁은 칸에 쓰는 축약 표기.
 */
export const PRESCRIPTION_EXCLUSION_REASON_OPTIONS = [
  { value: 'DUPLICATE_IMAGE', label: '중복 처방 이미지', shortLabel: '중복' },
  {
    value: 'INVALID_IMAGE',
    label: '잘못된 처방 이미지',
    shortLabel: '잘못된 이미지',
  },
] as const;

const findExclusionReason = (reason?: string | null) =>
  PRESCRIPTION_EXCLUSION_REASON_OPTIONS.find((opt) => opt.value === reason);

export const getExclusionReasonLabel = (reason?: string | null): string =>
  findExclusionReason(reason)?.label ?? reason ?? '-';

export const getExclusionReasonShortLabel = (reason?: string | null): string =>
  findExclusionReason(reason)?.shortLabel ?? reason ?? '-';

const APPROVAL_STATUS_LABEL: Record<string, string> = {
  APPROVED: '승인',
  REJECTED: '거절',
  PENDING: '대기',
  승인: '승인',
  '미등록 거래처': '미등록 거래처',
  '최소금액기준 미달': '최소금액기준 미달',
  // 레거시 Select 옵션 표기(공백 포함)도 동일 라벨로 정규화
  '최소 금액기준 미달': '최소금액기준 미달',
  '비거래 약물': '비거래 약물',
  '중복 입력': '중복 입력',
};

export type PrescriptionBadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'
  | 'info';

export const getApprovalStatusLabel = (status?: string): string =>
  (status && APPROVAL_STATUS_LABEL[status]) || status || '-';

/** 처방내역 승인 상태 태그 색 — 상태별 구분 */
export const getApprovalStatusBadgeVariant = (
  status?: string,
): PrescriptionBadgeVariant => {
  const label = getApprovalStatusLabel(status);
  switch (label) {
    case '승인':
      return 'success';
    case '미등록 거래처':
      return 'warning';
    case '최소금액기준 미달':
      return 'destructive';
    case '비거래 약물':
      return 'info';
    case '중복 입력':
      return 'warning';
    case '거절':
      return 'destructive';
    case '대기':
      return 'secondary';
    default:
      return 'outline';
  }
};

/** 기본 수수료율 태그 색 — 구간=info, 고정=warning (수수료율 화면과 동일) */
export const getBasicFeeTypeBadgeVariant = (
  value?: string | null,
): PrescriptionBadgeVariant => {
  if (value === 'RANGE' || value === '구간') return 'info';
  if (value === 'FIXED' || value === '고정') return 'warning';
  return 'secondary';
};

/** 상세 수수료율 태그 색 — 적용=success, 미적용=secondary */
export const getDetailFeeAppliedBadgeVariant = (
  value?: boolean | null,
): PrescriptionBadgeVariant => (value ? 'success' : 'secondary');

/** 레거시 prescriptionDetails 승인 상태 라디오 (API 값 = 한글 라벨) */
export const PRESCRIPTION_APPROVAL_FILTER_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '승인', value: '승인' },
  { label: '미등록 거래처', value: '미등록 거래처' },
  { label: '최소금액기준 미달', value: '최소금액기준 미달' },
  { label: '비거래 약물', value: '비거래 약물' },
] as const;

export type PrescriptionApprovalFilterValue =
  (typeof PRESCRIPTION_APPROVAL_FILTER_OPTIONS)[number]['value'];

export { formatYearMonth } from '@/shared/lib/dateFormat';

export const formatPrice = (value?: number | null): string => {
  if (value == null) return '-';
  return `₩${value.toLocaleString('ko-KR')}`;
};

/** 수량·금액 등 레거시 toLocaleString 표시 (통화 기호 없음) */
export const formatNumber = (value?: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('ko-KR');
};

/**
 * 원외 처방량·원내 사용량 표시.
 * 입력이 소수점 4자리까지 허용되므로(QuantityInput) 표시도 4자리까지 보여준다.
 * toLocaleString 기본값(최대 3자리)이면 1.2345가 1.235로 반올림돼 보인다.
 */
export const formatQuantity = (value?: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
};

/** 정산·표준수당율 — 소수(0.12) → 12.00% */
export const formatCommissionRate = (value?: number | null): string => {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(2)}%`;
};

/** 레거시 플랫폼 수수료율 — 이미 % 수치로 오는 값 */
export const formatPlatformFeeRate = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return `${Number(value).toFixed(2)}%`;
};

export const formatBasicFeeType = (value?: string | null): string => {
  if (!value) return '-';
  if (value === 'RANGE' || value === '구간') return '구간';
  if (value === 'FIXED' || value === '고정') return '고정';
  return value;
};

export const formatDetailFeeApplied = (value?: boolean | null): string =>
  value ? '적용' : '미적용';

export const getDefaultSettlementYm = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}${month}`;
};
